import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { getProject, projects } from '../data/projects';
import { EndNote } from '../components/EndNote';
import { SeoHead } from '../components/SeoHead';
type TabId = 'challenge' | 'approach' | 'outcome';
const tabs: {
  id: TabId;
  label: string;
}[] = [{
  id: 'challenge',
  label: 'Challenge'
}, {
  id: 'approach',
  label: 'Approach'
}, {
  id: 'outcome',
  label: 'Outcome'
}];
export function CaseStudy() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const project = slug ? getProject(slug) : undefined;
  const [tab, setTab] = useState<TabId>('challenge');
  if (!project) {
    return <div className="mx-auto max-w-page px-6 py-24 md:px-10" data-id="element-97" data-mp="37m09f7d7rosz">
        <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-6xl" data-id="element-98" data-mp="10v0wcna9yquz">
          We can&apos;t find
          <br data-id="element-99" data-mp="15ssvo2d91f97" />
          that project
        </h1>
        <Link to="/work" className="mt-7 inline-flex items-center gap-2 border-b-2 border-lilac pb-1 text-[0.7rem] uppercase tracking-micro transition-colors hover:text-lilac" data-id="element-100" data-mp="15rhri1fw65hk">
          <ArrowLeftIcon size={14} data-id="element-101" data-mp="3kxayitcbbk7r" />
          Back to work
        </Link>
      </div>;
  }
  const index = projects.findIndex(item => item.slug === project.slug);
  const next = projects[(index + 1) % projects.length];
  return <article data-id="element-102" data-mp="2t5d0gvfivcp1">
      <SeoHead title={`${project.title} — ${project.client} | Breezy Experiential`} description={`${project.summary} ${project.discipline} produced by Breezy Experiential in ${project.location}, ${project.year}.`} data-id="element-103" data-mp="2t6xsadagchj7" />

      <div className="mx-auto max-w-page px-6 pt-10 md:px-10 md:pt-14" data-id="element-104" data-mp="324girx6v7o2h">
        <Link to="/work" className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-micro text-muted transition-colors hover:text-sage" data-id="element-105" data-mp="2x66odosysrhx">
          <ArrowLeftIcon size={14} data-id="element-106" data-mp="cgpl90cne4yu" />
          All work
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6" data-id="element-107" data-mp="36a0d1e0d5l5f">
          <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-7xl" data-id="element-108" data-mp="3b8k1bl2fj90y">
            {project.title}
          </h1>
          <p className="max-w-sm leading-relaxed text-muted" data-id="element-109" data-mp="3fb8ajtv22hyg">
            {project.summary}
          </p>
        </div>

        {project.video ? <video src={project.video} poster={project.image || undefined} controls autoPlay loop muted playsInline aria-label={`${project.title} — ${project.client}`} className="mt-8 h-[42vh] w-full bg-cream/5 object-cover md:h-[62vh]" data-id="element-110" data-mp="3k6w1adp6rj62" /> : <img src={project.image} alt={`${project.title} — ${project.client}`} className="mt-8 h-[42vh] w-full object-cover md:h-[62vh]" data-id="element-111" data-mp="31byvwymhzvwl" />}

        <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-line pt-5 md:grid-cols-4" data-id="element-112" data-mp="28ss5d1z1fkyd">
          {[{
          label: 'Client',
          value: project.client
        }, {
          label: 'Location',
          value: project.location
        }, {
          label: 'Year',
          value: project.year
        }, {
          label: 'Discipline',
          value: project.discipline
        }].map(item => <div key={item.label} data-id="element-113" data-mp="1nbkliefc09z3">
              <dt className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-114" data-mp="1na9g6a68mu5v">
                {item.label}
              </dt>
              <dd className="mt-1.5 font-display text-lg font-semibold uppercase md:text-xl" data-id="element-115" data-mp="1idj5y3h46ssd">
                {item.value}
              </dd>
            </div>)}
        </dl>
      </div>

      <div className="mx-auto max-w-page px-6 py-14 md:px-10 md:py-20" data-id="element-116" data-mp="18jk3t4h1j90f">
        <div className="grid gap-8 md:grid-cols-12" data-id="element-117" data-mp="13oelefmukch3">
          <div className="md:col-span-3" data-id="element-118" data-mp="zlqam2ekovhh">
            <div role="tablist" aria-label="Case study sections" className="flex gap-2 md:flex-col md:items-start" data-id="element-119" data-mp="zkp0c05dsd0t">
              {tabs.map(item => {
              const active = tab === item.id;
              return <button key={item.id} role="tab" id={`tab-${item.id}`} aria-selected={active} aria-controls={`panel-${item.id}`} type="button" onClick={() => setTab(item.id)} className={`rounded-full px-4 py-2 text-[0.65rem] uppercase tracking-micro transition-colors ${active ? 'bg-sage text-ink' : 'text-muted hover:text-cream'}`} data-id="element-120" data-mp="2yooqgoye9a0z">
                    {item.label}
                  </button>;
            })}
            </div>

            <ul className="mt-8 space-y-1.5" data-id="element-121" data-mp="8vzwewftcx3">
              {project.scope.map(item => <li key={item} className="flex gap-2 text-sm text-cream/80" data-id="element-122" data-mp="56xg8s2hay4v">
                  <span className="text-lilac" data-id="element-123" data-mp="fcfi2kyiim56">✳</span>
                  {item}
                </li>)}
            </ul>
          </div>

          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="md:col-span-8 md:col-start-5" data-id="element-124" data-mp="aagbz3be1b4j">
            <p className="text-lg leading-relaxed md:text-2xl" data-id="element-125" data-mp="jbob1nlqafzo">
              {project[tab]}
            </p>
          </div>
        </div>
      </div>

      {project.gallery && <section className="mx-auto max-w-page px-6 pb-14 md:px-10 md:pb-20" data-id="element-126" data-mp="t1zeza5ewp6k">
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-127" data-mp="172tqw2ro589z">
            {project.gallery.map(src => <img key={src} src={src} alt="" aria-hidden="true" className="h-56 w-full object-cover md:h-80" data-id="element-128" data-mp="130ouwxdqzzk6" />)}
          </div>
        </section>}

      {project.stats && <section className="bg-sage text-ink" data-id="element-129" data-mp="17s77j6bq5asw">
        <div className="mx-auto grid max-w-page gap-8 px-6 py-12 md:grid-cols-3 md:px-10" data-id="element-130" data-mp="17tidncsn6un6">
          {project.stats.map(stat => <div key={stat.label} data-id="element-131" data-mp="1hpv8wf3u5gk1">
              <p className="font-display text-4xl font-bold uppercase leading-none md:text-5xl" data-id="element-132" data-mp="1hotr7rsbdvtc">
                {stat.value}
              </p>
              <p className="mt-2 text-[0.6rem] uppercase tracking-micro text-ink/60" data-id="element-133" data-mp="1mmlkoq8lxpzn">
                {stat.label}
              </p>
            </div>)}
        </div>
      </section>}

      {project.quote && <section className="mx-auto max-w-page px-6 py-16 md:px-10 md:py-24" data-id="element-134" data-mp="20ftcahwoulhc">
          <blockquote className="mx-auto max-w-3xl text-center" data-id="element-135" data-mp="25fg1zqtpdu71">
            <p className="font-display text-2xl font-medium uppercase leading-[1.1] md:text-4xl" data-id="element-136" data-mp="25ewkucbvn9u2">
              “{project.quote.text}”
            </p>
            <footer className="mt-6 text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-137" data-mp="2efuminyuf59h">
              {project.quote.attribution}
            </footer>
          </blockquote>
        </section>}

      <section className="border-t border-line" data-id="element-138" data-mp="3uwthmfh44i53">
        <Link to={`/work/${next.slug}`} className="group mx-auto flex max-w-page items-center justify-between gap-8 px-6 py-12 md:px-10 md:py-16" data-id="element-139" data-mp="3uv09evquppuz">
          <div data-id="element-140" data-mp="3kzfa55d633ox">
            <span className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-141" data-mp="3kzyj3wkuahqt">
              Next project
            </span>
            <p className="mt-2 font-display text-3xl font-bold uppercase leading-none transition-colors group-hover:text-sage md:text-5xl" data-id="element-142" data-mp="miktpkpjcfj9">
              {next.title}
            </p>
          </div>
          <ArrowRightIcon size={26} className="shrink-0 transition-transform duration-300 group-hover:translate-x-2" data-id="element-143" data-mp="colsqp1771sw" />
        </Link>
      </section>

      <EndNote data-id="element-144" data-mp="158ke3uafl6gc" />
    </article>;
}